// pages/_app.js
// Global styles and app-level setup.
// CSS imports MUST live here in Next.js — not in page files.

import '../styles/tokens.css';
import '../styles/layout.css';
import '../styles/components.css';
// Domain Intelligence — feature-scoped tokens (cool dark + Cormorant
// Garamond). All classes are namespaced under `.di-root` so they
// don't leak into other features.
import '../styles/intelligence.css';
// Workstation feature — same cool-dark palette family. All classes
// namespaced under .ws-* so they don't leak into other features.
import '../styles/workstation.css';
// QQQ Session Brief tab (day-trading only)
import '../styles/tab-qqq.css';
// QQQ Phase 2b TODAY-tab dashboard cards (Macro Frame Zone, Composite,
// Yellow Candle, F15 Levels, Vehicle Decision, Economic Calendar,
// News Feed, Open Observations). Loaded globally because the new
// orchestrator (features/workstations/qqq/today/) is rendered inside
// the legacy QqqTab during the parallel migration.
import '../features/workstations/qqq/today/today-cards.css';
// QQQ Pre-Breakout Pattern Research view (Intelligence tab).
import '../features/workstations/qqq/intel/pre-breakout-patterns.css';
// Workstation tone semantics — single source of truth for what
// each color MEANS (gold=AI, sage=working, teal=action, etc.).
// Components use .ws-pill-*, .ws-tone-*, .ws-callout-*, etc.
// instead of hard-coding color values.
import '../styles/workstation-tones.css';
// Workstation Signal Read — the marquee Workstation feature
// per the Platform Architecture doc. Two-column layout, dual
// direction output cards, analyzing state, history sidebar.
import '../styles/workstation-signal-read.css';
// Workstation People — personal CRM for the human layer of a
// domain. Card grid + add/edit modal + slide-in detail panel
// + Link-to-Person picker shared with Signal Read.
import '../styles/workstation-people.css';
// Workstation Works — structured outputs (trade plans,
// analyses, reviews, templates). 3-section lifecycle, slide-in
// editor, Link-to-Work picker shared with Signal Read.
import '../styles/workstation-works.css';
// Workstation Learn — daily lessons via the Three engine
// (curriculum-triggers + knowledge-generator), 4-part lesson
// cards with Helpful/Off/Known validation toggle.
import '../styles/workstation-learn.css';
// Workstation Force Tracker + Daily Read — Batch FT-2.
// 7-row force grid + Sonnet morning brief connecting today's
// classified forces to the user's specific criteria.
import '../styles/workstation-force-tracker.css';
// Workstation Signal Matrix — Batch FT-4. The visible BACKWARD
// pass: personal pattern_signals from the correlation engine,
// grouped by confidence with the math front-and-center.
import '../styles/workstation-signal-matrix.css';
// Workstation Dashboard Grid — Batch 3.5. Row-based smart grid
// with glowing 3D card chrome + drag-to-resize dividers + right
// sidebar customize panel. Uses @dnd-kit (not react-grid-layout).
import '../styles/workstation-dashboard-grid.css';
// Widget primitives — shared design language across all widgets
// (eyebrow, serif headline, section strip, action chip, misdir
// row, possib row, pill row, practitioner lens, etc.).
// Change these once → every widget that uses them updates.
import '../styles/widget-primitives.css';
// Jarvis Briefing widget — only Jarvis-specific styles
// (empty state, error banner, S1 typography). Most styling lives
// in widget-primitives.css.
import '../styles/widget-jarvis-briefing.css';
// Theses widget — Engine 2 capture + lifecycle UI (sibling of the
// briefing widget; theses stated here are what Engines 3/4/6
// link against).
import '../styles/widget-theses.css';
// Pre-Market Read — overnight + pre-open scan (Day Trading)
import '../styles/widget-pre-market.css';
// Thesis Stress Test — live thesis health monitor (Engines 2+3)
import '../styles/widget-thesis-stress-test.css';
// Sentiment — fear/greed + AAII + COT + short interest
import '../styles/widget-sentiment.css';
// Macro Conditions — yield curve + rates + dollar + commodities + econ
import '../styles/widget-macro-conditions.css';
// Outcome Calendar Heatmap — month grid of per-day P&L
import '../styles/widget-outcome-calendar.css';
// Kill Switch — Tier B safety widget
import '../styles/widget-kill-switch.css';
// Risk Budget — Tier B safety (soft boundary progress bars)
import '../styles/widget-risk-budget.css';
// Strategy Health Scorecard — Tier B meta (edge health rollup)
import '../styles/widget-strategy-health.css';
// Mistake Log — Tier B learning (reflective manual log)
import '../styles/widget-mistake-log.css';
// Decision Journal — Tier B learning (contemporaneous decisions)
import '../styles/widget-decision-journal.css';
// Morning Routine — Tier B rituals (pre-market prep checklist)
import '../styles/widget-morning-routine.css';
// Personal Playbook — Tier B learning (validated patterns one-pagers)
import '../styles/widget-personal-playbook.css';
// News Correlation — Tier D day-trading (ticker-matched feed items)
import '../styles/widget-news-correlation.css';
// 0DTE Conditions — Tier D day-trading (session + IV + PCR + V2 paid)
import '../styles/widget-dte-conditions.css';
// Strategic Dialogue — Tier A Jarvis Surface (persistent chat)
import '../styles/widget-strategic-dialogue.css';
// Flagged Companies — Tier D long-term workspace (triage queue)
import '../styles/widget-flagged-companies.css';
// Tier A Jarvis Surface (engine-display widgets)
import '../styles/widget-options-panel.css';     // J2 — Engine 4
import '../styles/widget-contradictions.css';    // J3 — Engine 3
import '../styles/widget-possibilities.css';     // J4 — Engine 6
import '../styles/widget-what-changed.css';      // J7 — Engine 1 diff
// Tier C — Pattern-Universal widgets
import '../styles/widget-position-sizing.css';   // C7 — Constrained Action Calculator
import '../styles/widget-rule-adherence.css';    // C16 — Rule-Adherence vs Outcome
import '../styles/widget-expected-vs-actual.css'; // C14 — Expected vs Actual
import '../styles/widget-decay-detector.css';     // C18 — Decay Detector
import '../styles/widget-allocation-drift.css';  // C9 — Allocation Drift Alerts
import '../styles/widget-restrategize-triggers.css'; // C19 — Restrategize Triggers
import '../styles/widget-event-calendar.css';     // C11 — External Event Calendar
import '../styles/widget-event-prep.css';         // C10 — Event Prep + Verdict
import '../styles/widget-benchmark-comparison.css'; // C17 — Benchmark Comparison
import '../styles/widget-workload-composition.css'; // C8 — Workload Composition
import '../styles/widget-exit-planner.css';       // C13 — Graduated Exit Planner
import '../styles/widget-context-adjusted.css';   // C15 — Context-Adjusted Performance
import '../styles/widget-discovery-inbox.css';    // C3 — Discovery Inbox
import '../styles/widget-entity-comparison.css';  // C6 — Multi-Entity Comparison
import '../styles/widget-gex-levels.css';         // FlashAlpha — GEX + dealer levels
// Macro Gate widget — only Macro-Gate-specific styles
// (gauge bar with sage/gold/terra zones + marker).
import '../styles/widget-macro-gate.css';
// Pipeline Intelligence widget — only the 5-stage progression strip.
// Pills + section + lens come from primitives.
import '../styles/widget-pipeline-intelligence.css';
// Pre-Explosion Matrix widget — only signal pills + matrix grid.
// Pills + section + lens come from primitives.
import '../styles/widget-pre-explosion-matrix.css';
// Force Tracker widget — warning chips + force list rows
// (clickable, expandable, tinted by direction). Pills + section
// + lens come from primitives.
import '../styles/widget-force-tracker.css';
// Process Checklist widget (Day Trading) — progress bar + clickable
// checklist rows. Chrome + section + lens come from primitives.
import '../styles/widget-process-checklist.css';
// Studies Confluence widget (Day Trading) — anchor-star marker +
// status pill per study row. Chrome + pills + section + lens
// come from primitives.
import '../styles/widget-studies.css';
// Window Risk Map widget (Day Trading) — band strip with tone
// tints + now marker. Chrome + section + lens come from primitives.
import '../styles/widget-window-risk-map.css';
// Today's Thesis widget (Day Trading) — setup chips row. Chrome +
// pills + section + lens come from primitives.
import '../styles/widget-thesis.css';
// Pattern Library Status widget (Day Trading) — per-setup rows
// with signal chips + tone-coded stats. Chrome + pills + section
// + lens come from primitives.
import '../styles/widget-pattern-library-status.css';
// Strategic Playbook — Domain #2. "The Study" theme: oak-paneled
// library aesthetic (oxblood + aged brass + parchment + serif
// headlines). All classes namespaced under .sp-root so they don't
// leak into other domains.
import '../styles/strategic-playbook.css';
// ─── Wealth Building (Domain #3) — "Compound" theme ──
// Sunrise / forward-time / young-empowerment aesthetic.
// All classes namespaced under .wb-root so they don't leak.
import '../styles/wealth-building.css';
import '../styles/wealth-intelligence.css';
import '../styles/wealth-screener.css';
// ─── Catalyst Research tab (Long-Term 10x workstation) ──
// Penny + catalyst sustain-vs-fade prediction methodology surface.
// All classes namespaced under .cat- so they don't leak.
import '../styles/catalyst.css';
import { I18nProvider } from '../hooks/use-i18n';
import { Literata } from 'next/font/google';

// Literata — a serif designed by TypeTogether specifically for Google
// Play Books and long-form digital reading. Closest open alternative to
// Kindle's Bookerly. Wider apertures and slightly looser letterforms
// than Georgia — easier on the eye during multi-hour reading sessions.
const literata = Literata({
  subsets: ['latin'],
  weight: ['400', '600', '700'],
  style: ['normal', 'italic'],
  display: 'swap',
});

export default function App({ Component, pageProps }) {
  return (
    <I18nProvider>
      {/* Publish Literata's actual font-family string as --font-literata
          at the :root level so tokens.css can reference it in
          --font-serif. This must be :root (not a wrapper className) so
          the var is visible to <body> and <html> where layout.css sets
          the base font. */}
      <style jsx global>{`
        :root {
          --font-literata: ${literata.style.fontFamily};
        }
      `}</style>
      <Component {...pageProps} />
    </I18nProvider>
  );
}
